import { CdkDrag } from '@angular/cdk/drag-drop';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  effect,
  inject,
  viewChild
} from '@angular/core';
import { RFQ_TOP_OF_BOOK, RfqDialog, RfqTab } from '@approot/models';
import { RfqMessagesService } from '@approot/rfq-messages/rfq-messages.service';
import {
  AdminService,
  EntityMapperService,
  RfqService,
  RfqTabService
} from '@approot/services';
import { RfqWsErrorMessage } from '@approot/services/rfq/rfq-tab/rfq-tab';
import { tabStatus } from '@approot/shared';
import { SubscriptionSink } from '@approot/utilities';
import { isEqual } from 'lodash';
import { Throttle } from 'lodash-decorators';
import { Subscription } from 'rxjs';
import { RfqSetupService } from './rfq-tab/rfq-setup/rfq-setup.service';
import { RfqTabStateService } from './rfq-tab/rfq-tab-state.service';
import { WindowIndexService } from './window-index.service';

const WIN_POSITION_MIN_X = 72;
const WIN_POSITION_MIN_Y = 36;
const WIN_POSITION_RIGHT_MIN = 100;
@Component({
  selector: 'app-rfq-container',
  templateUrl: './rfq-container.component.html',
  styleUrls: ['./rfq-container.component.scss'],
  animations: [tabStatus],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    RfqTabService,
    RfqTabStateService,
    RfqSetupService,
    RfqMessagesService
  ]
})
export class RfqContainerComponent
  implements OnInit, AfterContentChecked, OnDestroy
{
  @Input() public tabIndex: number;
  @Input() public tab: RfqTab;
  @ViewChild(CdkDrag) public dragEl: CdkDrag;
  public dragging = false;
  public rfqTabs: RfqTab[] = [];
  public productInfo: any;
  public cutOffTimesAndFixingSources: any;
  public tabSubjectSubscription: Subscription;
  public selectedEntity: string;
  public entities: string[];
  public windowTop = 50;
  public windowLeft = 50;
  public autoResize = true;
  public dialog: RfqDialog = {
    header: 'Notice',
    messages: [],
    onConfirm: null
  };

  private rfqContainer = viewChild<ElementRef>('rfqContainer');
  private cdInterval: any;
  private subsink = new SubscriptionSink();
  private entityMapperService = inject(EntityMapperService);
  private rfqErrorMsg: RfqWsErrorMessage;
  private rfeErrorMsg: RfqWsErrorMessage;

  constructor(
    private rfqService: RfqService,
    private rfqTabService: RfqTabService,
    private elementRef: ElementRef,
    private cdRef: ChangeDetectorRef,
    private adminService: AdminService,
    private renderer: Renderer2,
    private winIdx: WindowIndexService,
    private rfqMessageService: RfqMessagesService
  ) {
    this.cdRef.detach();
    // manually trigger change detection, just in case OnPush is broken.
    this.cdInterval = setInterval(() => {
      if (!this.dragging) {
        this.cdRef.detectChanges();
      }
    }, 500);
    this.subsink.sink = this.rfqTabService.windowCloser.subscribe({
      next: () => this.removeTab(this.tabIndex)
    });

    effect(_ => {
      const message = this.rfqMessageService.message();
      ['rfqError', 'rfeError'].forEach(errorKey => {
        const errorMsg = message[errorKey];
        const currentMsg =
          errorKey === 'rfqError' ? this.rfqErrorMsg : this.rfeErrorMsg;
        if (errorMsg && !isEqual(errorMsg, currentMsg)) {
          if (errorKey === 'rfqError') {
            this.rfqErrorMsg = errorMsg;
          } else {
            this.rfeErrorMsg = errorMsg;
          }

          if (
            !this.dialog.messages.find(
              msg => msg.message === errorMsg.content.message
            )
          ) {
            this.dialog.messages.push({
              message: errorMsg.content.message,
              messageType: 'error'
            });
          }
        }
      });
    });
  }

  /**
   * Every time the window is focused, update the z-index to 1 more than the current
   * highest z-index (slightly fragile as it assumes no components, other than similar
   * windows, have a zIndex > 2000)
   */
  public updateZIndex() {
    this.winIdx.setWindowIndex(
      this.elementRef.nativeElement.querySelector('.rfq-drag'),
      this.renderer
    );
  }

  public resetContainerHeight(): void {
    this.renderer.setStyle(
      this.rfqContainer().nativeElement,
      'height',
      `fit-content`
    );
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
    this.tabSubjectSubscription?.unsubscribe();
    // this.rfqService.wipeSessionData(); // unsub from rfq observables
    clearInterval(this.cdInterval);
  }

  public ngOnInit() {
    this.updateZIndex();
    this.entities = this.adminService.medusaCredentials.map(
      credential => credential.entity
    );
    if (this.adminService.medusaCredentials.length === 1) {
      // if only one medusa account is available, sign in with it
      this.selectedEntity = this.entities[0];
    }

    const elem = this.elementRef.nativeElement.querySelector(
      '.rfq-drag'
    ) as HTMLDivElement;

    this.windowTop = 5 + Math.random() * 10;
    this.windowLeft = 500 + Math.random() * (window.innerWidth / 4);
    this.windowLeft = Math.min(
      this.windowLeft,
      window.innerWidth - elem.clientWidth
    );
  }

  @Throttle(1000)
  public get canCloseWindow() {
    return this.rfqTabService.canCloseWindow;
  }

  public maybeCloseWindow() {
    if (this.rfqTabService.warnOnClose) {
      this.dialog.messages = [
        {
          message: `Warning: closing
this window without booking the trade may result in the trade being
incorrectly booked in your own systems.`,
          messageType: 'warning'
        }
      ];
      this.dialog.onConfirm = 'closeTab';
    } else {
      this.removeTab(this.tabIndex);
    }
  }

  public onDialogCancel(): void {
    this.dialog.messages = [];
    this.dialog.onConfirm = null;
  }

  public onDialogConfirm(): void {
    switch (this.dialog.onConfirm) {
      case 'closeTab':
        this.removeTab(this.tabIndex);
        break;
      default:
        break;
    }
    this.dialog.messages = [];
    this.dialog.onConfirm = null;
  }

  @Throttle(1000)
  public tabTitle() {
    return this.entityMapperService.getExtendedDisplayTitle(
      this.tab.title.account
    );
  }

  public dragStart() {
    this.dragging = true;
  }

  public async dragEnd() {
    this.dragging = false;
    const el = (this.elementRef.nativeElement as HTMLElement).querySelector(
      '.rfq-drag'
    );
    const rect = el.getBoundingClientRect();

    let { x, y } = this.dragEl.getFreeDragPosition();

    if (rect.x + rect.width < WIN_POSITION_MIN_X) {
      x += WIN_POSITION_MIN_X - (rect.x + rect.width);
    }
    if (rect.y < WIN_POSITION_MIN_Y) {
      y += WIN_POSITION_MIN_Y - rect.y;
    }
    if (rect.x > window.innerWidth - WIN_POSITION_RIGHT_MIN) {
      x -= rect.x - (window.innerWidth - WIN_POSITION_RIGHT_MIN);
    }
    this.dragEl.setFreeDragPosition({ x, y });
  }

  public get isRfqTabsBlocked() {
    return (
      this.rfqService.reLoginRequired || this.rfqService.blockNewRfqRequests
    );
  }

  public containerTabCommunication(action) {
    switch (action.command) {
      case RFQ_TOP_OF_BOOK:
        this.topOfBookSelected(action.data);
        break;
    }
  }

  public ngAfterContentChecked() {
    this.cdRef.detectChanges();
  }

  public topOfBookSelected(data: boolean) {
    const rfqContainerEl =
      this.elementRef.nativeElement.querySelector('.rfq-container');
    if (!data) {
      // if we are not on the top of book mode
      const bankRow = this.elementRef.nativeElement.querySelector(
        '.visible-section .table-column:not(.hide) .table-row-container'
      );

      if (bankRow) {
        if (rfqContainerEl.offsetHeight < 810) {
          this.renderer.setStyle(rfqContainerEl, 'height', 810 + 'px');
        }
      }
    } else {
      // if in top of book mode
      if (
        rfqContainerEl.offsetHeight !==
        this.tab.minDimensions.heightWithTopOfBook
      ) {
        // if the height isn't the height of the minimum it should be with only the top of book
        this.renderer.setStyle(
          rfqContainerEl,
          'height',
          this.tab.minDimensions.heightWithTopOfBook + 'px'
        );
      }
    }
  }

  public get minHeight(): number {
    if (this.tab.topOfBook) {
      // if the top of the book is selected
      // min height is the height in the top of book mode
      return this.tab.minDimensions.heightWithTopOfBook;
    } else {
      // use the min height of the tab
      return this.tab.minDimensions.height;
    }
  }

  public setStartingDimensions(tab: RfqTab) {}

  public saveCurrentDimensions() {
    if (this.rfqTabs[this.tabIndex]) {
      const contentEl =
        this.elementRef.nativeElement.querySelector('.rfq-container');
      // save the width and the height
      this.rfqTabs[this.tabIndex].dimensions.height = contentEl.offsetHeight;
      this.rfqTabs[this.tabIndex].dimensions.width = contentEl.offsetWidth;
    }
  }

  public removeTab(index: number) {
    this.rfqService.removeTab(index);
  }

  private qs(field: string): HTMLElement {
    return this.elementRef.nativeElement.querySelector(field) as HTMLElement;
  }
}
